import React, { useState, useEffect, useRef } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import {
  FaFilter,
  FaCalendarAlt,
  FaUserTie,
  FaDownload,
  FaSpinner,
  FaThumbsUp,
  FaStar,
  FaTrophy,
} from "react-icons/fa";
import SummaryApi from "../common";
import { toast } from "react-toastify";

// --- EMP IMAGES IMPORTS ---
import shivaji from "../assest/empimages/sivaji.png";
import durgapavani from "../assest/empimages/durga_pavani.png";
import sravani from "../assest/empimages/sravani.png";
import karuna from "../assest/empimages/karuna.png";
import sudha from "../assest/empimages/sudha_rani.png";
import anushag from "../assest/empimages/anusha_g.png";
import dhana from "../assest/empimages/dhanalakshmi.png";
import vara from "../assest/empimages/vara_lakshmi.png";
import navyap from "../assest/empimages/navya_p.png";
import manjusha from "../assest/empimages/manjusha.png";
import ramalakshmi from "../assest/empimages/ramalakshmi.png";
import yamuna from "../assest/empimages/yamuna.png";
import swethareddych from "../assest/empimages/swetha_reddy_ch.png";
import sukanya from "../assest/empimages/sukanya.png";
import anushai from "../assest/empimages/anusha_i.png";
import ramadevi from "../assest/empimages/ramadevi.png";
import sujatha from "../assest/empimages/sujatha.png";
import neelima from "../assest/empimages/neelima.png";
import harika from "../assest/empimages/harika.png";
import kamalapriya from '../assest/empimages/kamalapriya.png'
import bharathi from '../assest/empimages/bharathi.png'


// --- REGISTER CHARTJS COMPONENTS ---
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const BarChartStatus = () => {
  // --- STATE MANAGEMENT ---
  const [dateFilter, setDateFilter] = useState("today");
  const [customRange, setCustomRange] = useState({ start: "", end: "" });
  const [tlFilter, setTlFilter] = useState("");
  const [teamLeads, setTeamLeads] = useState([]);
  
  // State for Member Type (Normalized to lowercase)
  const [normalizedMemberType, setNormalizedMemberType] = useState("");

  // Chart specific state
  const [chartData, setChartData] = useState({ labels: [], datasets: [] });
  const [employeeData, setEmployeeData] = useState([]);
  const [loading, setLoading] = useState(false);
  const imagesRef = useRef({});

  // --- IMAGE MAPPING ---
  const empImages = {
    shivaji, durgapavani, sravani, karuna, sudha, sudharani: sudha,
    anushag, dhana, dhanalakshmi: dhana, vara, varalakshmi: vara,
    navyap, manjusha, ramalakshmi, yamuna, swethareddych, sukanya,
    anushai, ramadevi, sujatha, neelima, harika,  kamalapriya , bharathi
  };

  const getEmployeeImage = (empName) => {
    if (!empName) return "";
    const lowerName = empName.toLowerCase();
    const cleanName = lowerName.replace(/\s+/g, "");

    if (cleanName.includes("ramadevi")) return empImages.ramadevi;
    if (cleanName.includes("ramalakshmi")) return empImages.ramalakshmi;

    if (empImages[cleanName]) return empImages[cleanName];
    const cleanFirstName = empName.split(" ")[0].toLowerCase();
    if (empImages[cleanFirstName]) return empImages[cleanFirstName];

    return `https://ui-avatars.com/api/?name=${empName}&background=random&color=fff&size=128`;
  };

  // --- HELPER: FORMAT DATE ---
  const formatDate = (date) => {
    const d = new Date(date);
    const month = "" + (d.getMonth() + 1);
    const day = "" + d.getDate();
    const year = d.getFullYear();
    return [year, month.padStart(2, "0"), day.padStart(2, "0")].join("-");
  };

  // --- HELPER: GET DATE RANGE ---
  const getDateRange = () => {
    const today = new Date();
    let startDate = new Date();
    let endDate = new Date();

    if (dateFilter === "custom") {
      return { start_date: customRange.start, end_date: customRange.end };
    }

    switch (dateFilter) {
      case "last7":
        startDate.setDate(today.getDate() - 7);
        break;
      case "last15":
        startDate.setDate(today.getDate() - 15);
        break;
      case "last30":
        startDate.setDate(today.getDate() - 30);
        break;
      case "all":
        startDate = new Date("2020-01-01"); 
        break;
      default:
        break; // today
    }

    return { start_date: formatDate(startDate), end_date: formatDate(endDate) };
  };

  // --- 1. FETCH TEAM LEADS (Only for Admin/Managers) ---
  const fetchTeamLeads = async () => {
    try {
      const response = await fetch(SummaryApi.getAllTL.url, {
        method: SummaryApi.getAllTL.method,
        headers: {
          Authorization: "Bearer sandeep_university_916",
          "Content-Type": "application/json",
        },
      });
      const data = await response.json();
      if (data.status && data.data) {
        setTeamLeads(data.data);
        // Set default TL if none selected and NOT a Team Lead
        if (data.data.length > 0 && !tlFilter) {
          const defaultTL =
            data.data.find((tl) =>
              (tl.id || tl.employee_id).toString().startsWith("3")
            ) || data.data[0];
          setTlFilter(defaultTL.id || defaultTL.employee_id);
        }
      }
    } catch (error) {
      console.error("Error fetching TLs:", error);
    }
  };

  // --- 2. AUTH CHECK: MATCHING DASHBOARD SESSION LOGIC ---
  useEffect(() => {
    // Keys taken from Dashboard.js
    const MEMBER_TYPE_KEY = "K3M8X1JQ5VZ4B7PWTL9";
    const EMPLOYEE_ID_KEY = "H7Z4L9VD2XK5JW8QRYT6";

    const encodedMemberType = sessionStorage.getItem(MEMBER_TYPE_KEY);
    const encodedEmployeeId = sessionStorage.getItem(EMPLOYEE_ID_KEY);

    let role = "";
    let myId = "";

    // Decode if available
    if (encodedMemberType) {
      try {
        role = atob(encodedMemberType);
      } catch (e) {
        console.error("Error decoding member type", e);
      }
    }

    if (encodedEmployeeId) {
      try {
        myId = atob(encodedEmployeeId);
      } catch (e) {
        console.error("Error decoding employee id", e);
      }
    }

    // Normalize role for comparison
    const normalizedRole = role ? role.toLowerCase().trim() : "";
    setNormalizedMemberType(normalizedRole);

    if (normalizedRole === "team lead") {
      // Logic for Team Lead: Set their ID and DO NOT fetch the list
      if (myId) {
        setTlFilter(myId);
      } else {
        console.error("Team Lead detected but no ID found in session.");
      }
    } else {
      // Logic for Admin/Manager: Fetch the dropdown list
      fetchTeamLeads();
    }
  }, []);

  // --- 3. FETCH CHART DATA ---
  const fetchChartData = async () => {
    if (dateFilter === "custom" && (!customRange.start || !customRange.end))
      return;
    if (!tlFilter || tlFilter === "All Teams") return;

    setLoading(true);
    const { start_date, end_date } = getDateRange();

    try {
      const response = await fetch(SummaryApi.tlEmpStatusCount.url, {
        method: SummaryApi.tlEmpStatusCount.method,
        headers: {
          Authorization: "Bearer sandeep_university_916",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          team_lead_emp_id: tlFilter,
          start_date: start_date,
          end_date: end_date,
        }),
      });

      const data = await response.json();

      if (data.status && data.per_employee_counts) {
        setEmployeeData(data.per_employee_counts);
        console.log(
  "Employee Names:",
  data.per_employee_counts.map(emp => emp.employee_name)
);
        processChartData(data.per_employee_counts);
      } else {
        setChartData({ labels: [], datasets: [] });
        setEmployeeData([]);
        if (!data.status) toast.error(data.message || "No data found");
      }
    } catch (error) {
      console.error("Error fetching chart data:", error);
      toast.error("Failed to fetch performance data");
    } finally {
      setLoading(false);
    }
  };

  // --- 4. PROCESS DATA FOR CHART ---
  const processChartData = (employees) => {
    const labels = employees.map((emp) => emp.employee_name.split(" ")[0]);
    const imageUrls = employees.map((emp) =>
      getEmployeeImage(emp.employee_name)
    );

    const callbackData = employees.map((emp) => emp.call_back_count);
    const interestedData = employees.map((emp) => emp.interested_count);
    const mostInterestedData = employees.map(
      (emp) => emp.most_interested_count
    );

    setChartData({
      labels,
      datasets: [
        {
          label: "Callback",
          data: callbackData,
          images: imageUrls,
          backgroundColor: "#F59E0B",
          borderRadius: { topLeft: 0, topRight: 0, bottomLeft: 10, bottomRight: 10 },
          barThickness: 40,
        },
        {
          label: "Interested",
          data: interestedData,
          backgroundColor: "#3B82F6",
          barThickness: 40,
        },
        {
          label: "Most Interested",
          data: mostInterestedData,
          backgroundColor: "#10B981",
          borderRadius: { topLeft: 10, topRight: 10, bottomLeft: 0, bottomRight: 0 },
          barThickness: 40,
        },
      ],
    });
  };

  useEffect(() => {
    fetchChartData();
  }, [dateFilter, tlFilter, customRange]);

  // --- CUSTOM PLUGIN: DRAW IMAGES ---
  const avatarPlugin = {
    id: "avatarPlugin",
    afterDraw: (chart) => {
      const { ctx, scales } = chart;
      const { y } = scales;
      const avatarSize = 50; 

      if (!chart.data.datasets.length) return;
      const dataset0 = chart.data.datasets[0];
      if (!dataset0.images) return;

      chart.getDatasetMeta(0).data.forEach((bar, index) => {
        let totalValue = 0;
        chart.data.datasets.forEach((dataset) => {
          totalValue += Number(dataset.data[index] || 0);
        });

        if (totalValue === 0) return;

        const xPos = bar.x;
        const yPos = y.getPixelForValue(totalValue) - avatarSize / 2 - 12; 
        const imgUrl = dataset0.images[index];

        const drawImage = (imgObj) => {
          ctx.save();
          ctx.beginPath();
          ctx.arc(xPos, yPos, avatarSize / 2, 0, Math.PI * 2, true);
          ctx.closePath();
          ctx.clip();
          ctx.drawImage(imgObj, xPos - avatarSize / 2, yPos - avatarSize / 2, avatarSize, avatarSize);
          ctx.strokeStyle = "#ffffff";
          ctx.lineWidth = 3; 
          ctx.stroke();
          ctx.restore();

          ctx.font = "bold 12px sans-serif";
          ctx.fillStyle = "#374151";
          ctx.textAlign = "center";
          ctx.fillText(totalValue, xPos, yPos - avatarSize / 2 - 6);
        };

        if (imagesRef.current[imgUrl]) {
          drawImage(imagesRef.current[imgUrl]);
        } else {
          const img = new Image();
          img.src = imgUrl;
          img.crossOrigin = "Anonymous";
          img.onload = () => {
            imagesRef.current[imgUrl] = img;
            chart.draw();
          };
        }
      });
    },
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    layout: { padding: { top: 60 } },
    plugins: {
      legend: { display: true, position: "bottom" },
      tooltip: { mode: "index", intersect: false, backgroundColor: "rgba(0,0,0,0.8)" },
    },
    scales: {
      y: { stacked: true, beginAtZero: true, grid: { borderDash: [4, 4] } },
      x: { stacked: true, grid: { display: false }, ticks: { font: { weight: "bold", size: 13 }, color: "#374151" } },
    },
  };

  // --- CALCULATE TOP PERFORMERS ---
  const getTopPerformer = (data, key) => {
    if (!data || data.length === 0) return null;
    const top = data.reduce((prev, current) => prev[key] > current[key] ? prev : current);
    return top[key] > 0 ? top : null;
  };

  const topInterestedEmp = getTopPerformer(employeeData, "interested_count");
  const topMostInterestedEmp = getTopPerformer(employeeData, "most_interested_count");

  return (
    <div className="w-full bg-white p-6 rounded-2xl shadow-xl border border-gray-100 flex flex-col gap-8">
      {/* 1. HEADER & FILTERS */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
        <div>
          <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
            <span className="bg-indigo-100 p-2 rounded-lg text-indigo-600">
              <FaUserTie />
            </span>
            Employee Performance
          </h2>
          <p className="text-sm text-gray-500 mt-1 ml-1">
            Breakdown: Interested, Most Interested, & Callback
          </p>
        </div>

        <div className="flex flex-wrap items-end gap-3 bg-gray-50 p-3 rounded-xl border border-gray-200">
          
          {/* CHANGE: Display Select ONLY if user is NOT a Team Lead */}
          {normalizedMemberType !== "team lead" && (
            <div className="flex flex-col">
              <label className="text-xs font-bold text-gray-600 mb-1 ml-1">Select Team Lead</label>
              <div className="relative group">
                <FaFilter className="absolute left-3 top-3 text-gray-400 text-xs" />
                <select
                  className="pl-8 pr-4 py-2 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-700 focus:ring-2 focus:ring-indigo-500 focus:outline-none cursor-pointer min-w-[180px]"
                  value={tlFilter}
                  onChange={(e) => setTlFilter(e.target.value)}
                >
                  <option value="" disabled>
                    -- Select Team Lead --
                  </option>
                  {teamLeads.map((tl) => (
                    <option
                      key={tl.id || tl.employee_id}
                      value={tl.id || tl.employee_id}
                    >
                      {tl.full_name || tl.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Date Filter */}
          <div className="flex flex-col">
             <label className="text-xs font-bold text-gray-600 mb-1 ml-1">Date Range</label>
            <div className="relative">
              <FaCalendarAlt className="absolute left-3 top-3 text-gray-400 text-xs" />
              <select
                className="pl-8 pr-4 py-2 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-700 focus:ring-2 focus:ring-indigo-500 focus:outline-none cursor-pointer"
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
              >
                <option value="today">Today</option>
                <option value="last7">Last 7 Days</option>
                <option value="last15">Last 15 Days</option>
                <option value="last30">Last 30 Days</option>
                <option value="all">All Time</option>
                <option value="custom">Custom Date</option>
              </select>
            </div>
          </div>

          {dateFilter === "custom" && (
            <div className="flex flex-col">
              <label className="text-xs font-bold text-gray-600 mb-1 ml-1">Select Range</label>
              <div className="flex items-center gap-2">
                <input
                  type="date"
                  className="px-3 py-2 bg-white border border-gray-200 rounded-lg text-xs"
                  onChange={(e) =>
                    setCustomRange({ ...customRange, start: e.target.value })
                  }
                />
                <span className="text-gray-400">-</span>
                <input
                  type="date"
                  className="px-3 py-2 bg-white border border-gray-200 rounded-lg text-xs"
                  onChange={(e) =>
                    setCustomRange({ ...customRange, end: e.target.value })
                  }
                />
              </div>
            </div>
          )}

          {/* <div className="flex flex-col justify-end h-full">
            <button className="p-2.5 mb-[1px] bg-indigo-50 text-indigo-600 rounded-lg hover:bg-indigo-100 transition-colors">
              <FaDownload size={14} />
            </button>
          </div> */}
        </div>
      </div>

      {/* 2. CHART SECTION */}
      <div className="relative h-[400px] w-full bg-gradient-to-b from-transparent to-gray-50/50 rounded-xl p-4">
        {loading ? (
          <div className="h-full flex flex-col items-center justify-center text-gray-400">
            <FaSpinner className="animate-spin text-2xl mb-2 text-indigo-500" />
            <p>Loading Data...</p>
          </div>
        ) : chartData.datasets.length > 0 ? (
          <Bar data={chartData} options={options} plugins={[avatarPlugin]} />
        ) : (
          <div className="h-full flex flex-col items-center justify-center text-gray-400">
            <p>No data available for the selected range/team.</p>
          </div>
        )}
      </div>

      {/* 3. TOP PERFORMERS SECTION */}
      {!loading && employeeData.length > 0 && (topInterestedEmp || topMostInterestedEmp) && (
        <div className="border-t border-gray-100 pt-6">
          <h3 className="text-lg font-bold text-gray-700 mb-4 flex items-center gap-2">
            <FaTrophy className="text-yellow-500" /> Top Performers
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Top Interested Card */}
            {topInterestedEmp && (
              <div className="bg-blue-50 border border-blue-100 p-6 rounded-xl flex items-center gap-6 shadow-sm hover:shadow-md transition-shadow">
                <div className="relative">
                  <img
                    src={getEmployeeImage(topInterestedEmp.employee_name)}
                    alt={topInterestedEmp.employee_name}
                    className="w-16 h-16 rounded-full border-4 border-white shadow-sm object-cover"
                  />
                  <div className="absolute -bottom-2 -right-2 bg-blue-600 text-white p-1.5 rounded-full text-xs">
                    <FaThumbsUp />
                  </div>
                </div>
                <div>
                  <p className="text-blue-600 font-bold text-xs uppercase tracking-wider mb-1">
                    Top Interested
                  </p>
                  <h4 className="text-xl font-bold text-gray-800">
                    {topInterestedEmp.employee_name}
                  </h4>
                  <p className="text-sm text-gray-500">
                    Count:{" "}
                    <span className="font-bold text-blue-700">
                      {topInterestedEmp.interested_count}
                    </span>
                  </p>
                </div>
              </div>
            )}

            {/* Top Most Interested Card */}
            {topMostInterestedEmp && (
              <div className="bg-emerald-50 border border-emerald-100 p-6 rounded-xl flex items-center gap-6 shadow-sm hover:shadow-md transition-shadow">
                <div className="relative">
                  <img
                    src={getEmployeeImage(topMostInterestedEmp.employee_name)}
                    alt={topMostInterestedEmp.employee_name}
                    className="w-16 h-16 rounded-full border-4 border-white shadow-sm object-cover"
                  />
                  <div className="absolute -bottom-2 -right-2 bg-emerald-600 text-white p-1.5 rounded-full text-xs">
                    <FaStar />
                  </div>
                </div>
                <div>
                  <p className="text-emerald-600 font-bold text-xs uppercase tracking-wider mb-1">
                    Top Most Interested
                  </p>
                  <h4 className="text-xl font-bold text-gray-800">
                    {topMostInterestedEmp.employee_name}
                  </h4>
                  <p className="text-sm text-gray-500">
                    Count:{" "}
                    <span className="font-bold text-emerald-700">
                      {topMostInterestedEmp.most_interested_count}
                    </span>
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default BarChartStatus;